import React from 'react';
import { ArrowRight, Play, MonitorPlay, GraduationCap, Award } from 'lucide-react';

const Hero = () => {
  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-black">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-teal-950/20 via-black to-black" />
      
      {/* Subtle grid pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
          backgroundSize: '100px 100px'
        }} />
      </div>

      {/* Background image overlay */}
      <div className="absolute inset-0 opacity-10">
        <img
          src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=1600"
          alt="Business education"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
        {/* Logo */}
        <div className="mb-12 flex justify-center animate-fade-in">
          <div className="relative logo-container-animated">
            {/* Multiple glow layers for more dramatic effect */}
            <div className="absolute -inset-8 bg-gradient-to-r from-teal-500/30 via-teal-400/40 to-teal-500/30 rounded-full blur-3xl animate-pulse-glow" />
            <div className="absolute -inset-4 bg-teal-500/40 rounded-full blur-2xl animate-pulse-fast" />
            
            {/* Animated scan line effect */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute w-full h-1 bg-gradient-to-r from-transparent via-teal-400 to-transparent animate-scan-line" style={{ top: '0' }} />
            </div>
            
            {/* Energy pulse rings */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-full h-full border-2 border-teal-400/20 rounded-full animate-energy-pulse" />
              <div className="absolute w-full h-full border-2 border-teal-400/30 rounded-full animate-energy-pulse-delay" />
            </div>
            
            {/* Light beams */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
              <div className="absolute top-1/2 left-1/2 w-1 h-full bg-gradient-to-b from-teal-400/40 to-transparent animate-light-beam-1" style={{ transform: 'translate(-50%, -50%) rotate(45deg)' }} />
              <div className="absolute top-1/2 left-1/2 w-1 h-full bg-gradient-to-b from-teal-400/40 to-transparent animate-light-beam-2" style={{ transform: 'translate(-50%, -50%) rotate(-45deg)' }} />
            </div>
            
            <img
              src="https://customer-assets.emergentagent.com/job_knowledge-hub-196/artifacts/464mphrk_Recurso%2026LOGO%20def.png"
              alt="Urpe School Logo"
              className="relative h-64 md:h-72 lg:h-80 w-auto animate-logo-float-big hover:scale-105 transition-all duration-700 filter drop-shadow-2xl"
              style={{ filter: 'drop-shadow(0 0 30px rgba(20, 184, 166, 0.6))' }}
            />
          </div>
        </div>

        {/* Main heading */}
        <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-6 tracking-tight animate-slide-up">
          <span className="relative inline-block">
            <span className="absolute inset-0 bg-gradient-to-r from-teal-400 via-white to-teal-400 bg-clip-text text-transparent blur-sm animate-text-glow-pulse" aria-hidden="true">
              URPE SCHOOL
            </span>
            <span className="relative bg-gradient-to-r from-white via-teal-100 to-white bg-clip-text text-transparent animate-gradient-shift bg-[length:200%_100%]">
              URPE SCHOOL
            </span>
          </span>
          <br />
          <span className="text-3xl md:text-4xl lg:text-5xl font-light text-gray-300 mt-2 block animate-fade-in-delay">
            of Liberty
          </span>
        </h1>

        {/* Subtitle */}
        <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto font-light leading-relaxed animate-fade-in-delay">
          Donde los emprendedores aprenden a pensar, construir y escalar con libertad.
        </p>

        {/* Feature badges */}
        <div className="flex flex-wrap justify-center gap-4 mb-12 animate-fade-in-delay-2">
          <div className="flex items-center space-x-2 px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full">
            <MonitorPlay size={18} className="text-teal-400" />
            <span className="text-sm text-gray-300">100% Online</span>
          </div>
          <div className="flex items-center space-x-2 px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full">
            <GraduationCap size={18} className="text-teal-400" />
            <span className="text-sm text-gray-300">Emprendedores Activos</span>
          </div>
          <div className="flex items-center space-x-2 px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full">
            <Award size={18} className="text-teal-400" />
            <span className="text-sm text-gray-300">Certificado Verificable</span>
          </div>
        </div>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in-delay-3">
          <a
            href="#admissions"
            className="group px-8 py-4 bg-gradient-to-r from-teal-500 to-teal-600 text-black font-semibold rounded-full hover:from-teal-400 hover:to-teal-500 transition-all duration-300 shadow-2xl shadow-teal-500/30 hover:shadow-teal-500/50 hover:scale-105 flex items-center space-x-2"
          >
            <span>Aplicar a la Beca Libertad</span>
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-teal-400 rounded-full animate-scroll" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
